'use client'

import { useState } from 'react'
import Reveal from '../Reveal'
import { submitContact } from '@/lib/api'

type Status = 'idle' | 'loading' | 'success' | 'error'

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setError('')
    try {
      await submitContact(form)
      setStatus('success')
      setForm({ name: '', email: '', message: '' })
    } catch (err) {
      setStatus('error')
      setError(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  return (
    <section id="contact" className="relative py-24">
      <div className="absolute inset-0 bg-radial-glow opacity-50 pointer-events-none" />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="text-xs tracking-[0.3em] text-electric-400 uppercase mb-3">
            Talk to us
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-chrome leading-tight">
            Have a question?
            <br />Our desk is on it.
          </h2>
          <p className="mt-5 text-steel-300 leading-relaxed max-w-md">
            Whether you're borrowing $5k or managing an eight-figure treasury, our
            lending specialists reply within one business hour.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { label: 'Support', value: 'support@cryptocredits.io' },
              { label: 'Institutional desk', value: 'otc@cryptocredits.io' },
            ].map((c) => (
              <div key={c.label} className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl glass flex items-center justify-center text-electric-300">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="text-xs text-steel-400">{c.label}</div>
                  <div className="text-white font-medium">{c.value}</div>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={onSubmit} className="glass rounded-3xl p-7 sm:p-9 space-y-5">
            <div>
              <label className="block text-sm text-steel-300 mb-2">Name</label>
              <input
                name="name"
                required
                minLength={2}
                value={form.name}
                onChange={onChange}
                placeholder="Satoshi Nakamoto"
                className="w-full px-4 py-3 rounded-xl bg-ink-soft border border-steel-700/60 text-white placeholder-steel-500 focus:border-electric-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-steel-300 mb-2">Email</label>
              <input
                name="email"
                type="email"
                required
                value={form.email}
                onChange={onChange}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl bg-ink-soft border border-steel-700/60 text-white placeholder-steel-500 focus:border-electric-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm text-steel-300 mb-2">Message</label>
              <textarea
                name="message"
                required
                minLength={10}
                rows={4}
                value={form.message}
                onChange={onChange}
                placeholder="I'd like to borrow against my ETH holdings…"
                className="w-full px-4 py-3 rounded-xl bg-ink-soft border border-steel-700/60 text-white placeholder-steel-500 focus:border-electric-500 focus:outline-none transition-colors resize-none"
              />
            </div>

            {status === 'error' && (
              <p className="text-sm text-red-400">{error}</p>
            )}
            {status === 'success' && (
              <p className="text-sm text-emerald-400">
                Thanks — we've received your message and will be in touch shortly.
              </p>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              className="btn-shimmer w-full inline-flex items-center justify-center px-6 py-3.5 rounded-xl font-semibold bg-gradient-to-b from-electric-400 to-electric-600 text-white shadow-glow hover:shadow-glow-lg transition-shadow disabled:opacity-60"
            >
              {status === 'loading' ? 'Sending…' : 'Send message'}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  )
}
